// The signed-in session (Cognito sign-in): the ID token the API takes, a refresh token that keeps it alive, and the expiry,
// kept in localStorage, read through try/catch, with a copy in memory so a browser that refuses storage still keeps it for
// the page. Everything here is plain functions plus one hook, so the pages, the request helpers and the tests share one
// source of truth. No token is ever logged.
import { useEffect, useSyncExternalStore } from 'react';
import type { StaffProfile, StaffRole } from '@shared/types';

export const STAFF_SESSION_KEY = 'rounds.auth';
/** Where the magic-link sessions were kept; forgotten on the first read so no stale session lingers. */
const OLD_SESSION_KEY = 'rounds.staff.session';
/** A session is refreshed this long before its ID token runs out. */
export const REFRESH_AHEAD_MS = 2 * 60_000;
/** A session counts as over this long before its expiry, so a request never goes out on its last breath. */
export const EXPIRY_MARGIN_MS = 30_000;
const ROLES: readonly StaffRole[] = ['admin', 'manager', 'staff', 'clinician', 'ops', 'teacher', 'creator'];
const MAX_TIMER_MS = 2_147_000_000; // setTimeout's ceiling (about 24.8 days)

let memRaw: string | null = null;      // the session as last saved here (used when storage is refused or wiped)
let parsedFor: string | null | undefined; // the raw value `parsed` was read from
let parsed: StaffSession | undefined;
let endedHere = false;                 // the session in this tab ran out or was refused, not signed out (set as a revival starts)
let revivalFor: string | undefined;    // the ID token whose revival has started: each stored token is revived at most once
let oldKeyGone = false;                // the old key is removed on the first read after the page loads, not on every read
const listeners = new Set<() => void>();

/** `token` is the Cognito ID token (what the API takes as Authorization: Bearer); `refreshToken` trades for a new one. */
export interface StaffSession { token: string; refreshToken?: string; sub?: string; email: string; name: string; roles: StaffRole[]; expiresAt: number }

const quietly = <T>(fn: () => T): T | undefined => { try { return fn(); } catch { return undefined; } };
const notify = () => { for (const l of [...listeners]) l(); };

/** A stored or returned session, checked field by field; undefined unless it is whole. Expiry in seconds becomes milliseconds. */
export function normaliseSession(raw: unknown): StaffSession | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (typeof r.token !== 'string' || !r.token || typeof r.email !== 'string' || !r.email) return undefined;
  if (!Array.isArray(r.roles) || typeof r.expiresAt !== 'number' || !Number.isFinite(r.expiresAt)) return undefined;
  const roles = ROLES.filter((role) => (r.roles as unknown[]).includes(role));
  const expiresAt = r.expiresAt < 1e12 ? r.expiresAt * 1000 : r.expiresAt;
  return {
    token: r.token, ...(typeof r.refreshToken === 'string' && r.refreshToken ? { refreshToken: r.refreshToken } : {}), ...(typeof r.sub === 'string' && r.sub ? { sub: r.sub } : {}),
    email: r.email, name: typeof r.name === 'string' && r.name.trim() ? r.name.trim() : r.email, roles, expiresAt,
  };
}

/** The claims a Cognito ID token carries (read, not verified: the API verifies every request). */
function claimsOf(idToken: string): Record<string, unknown> {
  try {
    const part = idToken.split('.')[1] ?? '';
    const bytes = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(part.length + ((4 - (part.length % 4)) % 4), '='));
    // atob gives one character per byte; the payload is UTF-8, so a name like Zoë needs its bytes decoded together.
    const c = JSON.parse(new TextDecoder().decode(Uint8Array.from(bytes, (ch) => ch.charCodeAt(0)))) as unknown;
    return c && typeof c === 'object' ? (c as Record<string, unknown>) : {};
  } catch { return {}; }
}
/** A session from Cognito's tokens: the ID token is what the API takes; the refresh token keeps it alive. */
export function sessionFromTokens(t: { idToken: string; accessToken: string; refreshToken?: string; expiresAt: number }): StaffSession {
  const c = claimsOf(t.idToken);
  const email = typeof c.email === 'string' ? c.email.trim().toLowerCase() : '';
  const groups = Array.isArray(c['cognito:groups']) ? (c['cognito:groups'] as unknown[]) : [];
  return {
    token: t.idToken, ...(t.refreshToken ? { refreshToken: t.refreshToken } : {}), ...(typeof c.sub === 'string' ? { sub: c.sub } : {}),
    email, name: typeof c.name === 'string' && c.name.trim() ? c.name.trim() : email, roles: ROLES.filter((r) => groups.includes(r)), expiresAt: t.expiresAt,
  };
}

function rawNow(): string | null {
  if (!oldKeyGone) { oldKeyGone = true; quietly(() => localStorage.removeItem(OLD_SESSION_KEY)); }
  const stored = quietly(() => localStorage.getItem(STAFF_SESSION_KEY));
  return stored === undefined ? memRaw : stored ?? memRaw;
}
function write(raw: string | null) {
  memRaw = raw;
  revivalFor = undefined; // what is written here replaces or removes the session that was being revived
  quietly(() => (raw === null ? localStorage.removeItem(STAFF_SESSION_KEY) : localStorage.setItem(STAFF_SESSION_KEY, raw)));
}
const expired = (s: StaffSession, now = Date.now()) => s.expiresAt - EXPIRY_MARGIN_MS <= now;

/** The session as stored, expired or not (undefined when none or unreadable). */
function stored(): StaffSession | undefined {
  const raw = rawNow();
  if (raw !== parsedFor) { parsedFor = raw; parsed = raw ? normaliseSession(quietly(() => JSON.parse(raw))) : undefined; }
  return parsed;
}

/** The signed-in session, or undefined when there is none or it has run out. Stable between changes (a store snapshot). */
export function getStaffSession(now = Date.now()): StaffSession | undefined {
  const s = stored();
  if (!s) return undefined;
  // Read during a render: the other listeners are told just after, never mid-render.
  if (expired(s, now)) { sessionRanOut(s, () => queueMicrotask(notify)); return undefined; }
  return s;
}

/** The expiry timer and the tab coming back (focus, visibility) look for a session that ran out meanwhile. */
function expireIfDue() {
  const s = stored();
  if (s && expired(s)) sessionRanOut(s, notify);
}

/**
 * The one place a session that has run out is dealt with, whoever noticed. Without a refresh token it is forgotten. With
 * one it is revived: it reads as signed out until Cognito answers, and refreshNow then saves the new session or forgets
 * this one. The revival starts once per token, so reading the store again changes nothing and tells nobody (a notify on
 * every read would loop through the listeners for ever). `tell` notifies the listeners, later when the caller is a render.
 */
function sessionRanOut(s: StaffSession, tell: () => void): void {
  if (!s.refreshToken) { forgetEnded(tell); return; }
  if (revivalFor === s.token) return;
  revivalFor = s.token;
  endedHere = true; // a gate sends people to sign in meanwhile, and sign-in says why
  tell();
  // Started after the render or event that noticed, never during it.
  queueMicrotask(() => { void refreshNow(s); });
}

/** Forgets the session because it ran out or was refused, not signed out, so sign-in can say why it is back. */
function forgetEnded(tell: () => void = notify) {
  write(null);
  endedHere = true;
  tell();
}

export function saveStaffSession(s: StaffSession): void {
  const clean = normaliseSession(s);
  if (!clean) return;
  endedHere = false;
  write(JSON.stringify(clean));
  notify();
}

let refreshing: Promise<void> | undefined;
/**
 * Trades the session's refresh token for new tokens, one request at a time in the tab. Only the session asked about is
 * touched: the new tokens replace it; if they cannot be had, a session that has run out is forgotten (the revival failed),
 * and one that has not is left to its expiry, which tries once more.
 */
export function refreshNow(s: StaffSession): Promise<void> {
  const { refreshToken } = s;
  // Returned before the shared promise is set: an async body that ends without awaiting settles at once, and a settled
  // promise stored there would never be cleared, so no later refresh in the tab would ever go out.
  if (!refreshToken) return Promise.resolve();
  refreshing ??= (async () => {
    try {
      const next = await renewed(refreshToken);
      const held = stored();
      // Someone may have signed in again, or out, while Cognito was answering: their session, or its absence, stands.
      if (held?.token !== s.token) return;
      if (next) saveStaffSession(next);
      else if (expired(held)) forgetEnded();
    } finally { refreshing = undefined; }
  })();
  return refreshing;
}

/** The session new tokens make; undefined when Cognito refuses, the network is down, or the answer is no use as a session. */
async function renewed(refreshToken: string): Promise<StaffSession | undefined> {
  try {
    // Imported here so pages that only read the session never load the Cognito client.
    const { refresh } = await import('@/auth/cognito');
    const t = await refresh(refreshToken);
    // An ID token we cannot read has no address, and tokens already over (clock skew, a short lifetime) would only end again.
    const next = normaliseSession(sessionFromTokens({ ...t, refreshToken: t.refreshToken ?? refreshToken }));
    return next && !expired(next) ? next : undefined;
  } catch { return undefined; }
}

/** Fresh name and roles from GET /auth/me, for the same session only (a newer sign-in in another tab is left alone). */
export function updateStaffProfile(token: string, p: StaffProfile): void {
  const s = stored();
  if (!s || s.token !== token) return;
  const next = normaliseSession({ ...s, name: p.name, roles: p.roles, expiresAt: typeof p.expiresAt === 'number' ? p.expiresAt : s.expiresAt, token });
  if (!next || JSON.stringify(next) === JSON.stringify(s)) return;
  write(JSON.stringify(next));
  notify();
}

/** Signing out: the session goes, and nothing says it ended by itself. */
export function clearStaffSession(): void {
  endedHere = false;
  write(null);
  notify();
}

/**
 * The server refused this session, which is forgotten if it is still the one held (removed from the team, signed out
 * everywhere). A refused ID token that has run out says nothing new: it goes the way of any session found over, revived
 * when it can be, so a request racing the revival never cancels it.
 */
export function endStaffSession(token: string): void {
  const s = stored();
  if (s?.token !== token) return;
  if (expired(s)) sessionRanOut(s, notify);
  else forgetEnded();
}

/** Whether the last session in this tab ended by itself or was refused, so sign-in can say why it is back. */
export const sessionEndedHere = () => endedHere;

export function subscribeStaffSession(fn: () => void): () => void {
  listeners.add(fn);
  if (listeners.size === 1) {
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', expireIfDue);
    document.addEventListener('visibilitychange', expireIfDue);
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', expireIfDue);
      document.removeEventListener('visibilitychange', expireIfDue);
    }
  };
}
function onStorage(e: StorageEvent) {
  if (e.key !== null && e.key !== STAFF_SESSION_KEY) return;
  memRaw = e.key === null ? null : e.newValue; // key null: the other tab cleared all storage
  // A session from another tab (a sign-in or a revival there) takes over, so a later sign-out is not taken for an expiry.
  if (memRaw !== null) endedHere = false;
  notify();
}

/** The signed-in session for a component: re-renders on sign-in, sign-out (here or in another tab) and when it runs out. */
export function useStaffSession(): StaffSession | undefined {
  const s = useSyncExternalStore(subscribeStaffSession, getStaffSession, () => undefined);
  useEffect(() => {
    if (!s) return;
    const now = Date.now();
    // A refresh token moves the expiry on a little before it comes; at the expiry itself the session is revived or forgotten.
    const ends = window.setTimeout(expireIfDue, Math.min(MAX_TIMER_MS, Math.max(0, s.expiresAt - EXPIRY_MARGIN_MS - now) + 50));
    const renews = s.refreshToken
      ? window.setTimeout(() => { void refreshNow(s); }, Math.min(MAX_TIMER_MS, Math.max(0, s.expiresAt - REFRESH_AHEAD_MS - now)))
      : undefined;
    return () => { window.clearTimeout(ends); if (renews !== undefined) window.clearTimeout(renews); };
  }, [s]);
  return s;
}

export const hasRole = (s: Pick<StaffProfile, 'roles'> | undefined, role: StaffRole) => Boolean(s?.roles.includes(role));

// ---------------------------------------------------------------------------
// Where to land after signing in
// ---------------------------------------------------------------------------
/** Where a signed-in person lands when nowhere in particular was asked for: Home, whoever they are (12 Oct). */
export const DEFAULT_NEXT = '/home';
/**
 * A same-site path to land on after sign-in: it starts with one '/', never '//', a backslash or a control character, and
 * is never a sign-in or registration page itself. A fragment is dropped (a key in it must never travel in an email). Anything else: Home.
 */
export function safeNext(raw: unknown, fallback = DEFAULT_NEXT): string {
  if (typeof raw !== 'string') return fallback;
  const path = raw.split('#')[0];
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\') || path.length > 512) return fallback;
  if (/[\u0000-\u001f\u007f]/.test(path)) return fallback;
  // Case-blind, as the router matches paths; the fragment is gone already.
  if (/^\/(sign-in|register|staff\/(sign-in|verify))(?:[/?]|$)/i.test(path)) return fallback;
  return path;
}

/** The roles that make someone MedSnapp staff, listed on Team (creator and teacher only raise a creator's limits). */
export const STAFF_ROLES: readonly StaffRole[] = ['admin', 'manager', 'staff', 'clinician', 'ops'];
export const isStaffMember = (roles: readonly StaffRole[] | undefined) => Boolean(roles?.some((r) => STAFF_ROLES.includes(r)));
/**
 * Where a person lands after signing in: Home when nowhere in particular was asked for (12 Oct: everyone, staff
 * included). Staff go where they were going, except a member without the staff role headed only for the builder, who
 * lands on Home instead (the server would refuse them the builder). Everyone else goes where they were going when it is
 * one of their own pages (Make a battle, their battles, the open list, Home, their account, a result page, the game
 * they left to sign in from, whose address carries their place, or a battle's join page), and to Home otherwise.
 */
export function landingFor(roles: readonly StaffRole[] | undefined, next: string): string {
  const path = safeNext(next);
  if (isStaffMember(roles)) return /^\/(studio|builder)\/?$/.test(path) && !roles?.includes('staff') ? DEFAULT_NEXT : path;
  return /^\/(create|open|home|account|r|play|j)(?:[/?]|$)/.test(path) ? path : DEFAULT_NEXT;
}

/** The sign-in page, with where to come back to and, when a session has just ended, why we are back. */
export function signInPath(next: string, why?: 'expired'): string {
  const q = new URLSearchParams({ next: safeNext(next) });
  if (why) q.set('why', why);
  return `/sign-in?${q.toString()}`;
}
